import type { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePageFAQ, getVenuePageFAQs } from "../../data/venue_pages";

import { SupabaseClient } from "@supabase/supabase-js";

export function useVenuePageFAQs(venuePageId: string): {
  faqs: VenuePageFAQ[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryFn = async (): Promise<VenuePageFAQ[]> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const faqs = await getVenuePageFAQs(supabase, venuePageId);
      return faqs;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `error fetching FAQs for venue page: ${venuePageId}`,
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
          supabase: true,
        },
      });
      throw error;
    }
  };

  const {
    data: faqs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venue-page-faqs", venuePageId],
    queryFn,
  });

  return { faqs, isLoading, error, refetch };
}

export async function prefetchVenuePageFAQs(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  await queryClient.prefetchQuery({
    queryKey: ["venue-page-faqs", venuePageId],
    queryFn: async () => {
      return await getVenuePageFAQs(supabase, venuePageId);
    },
    staleTime: 5 * 60 * 1000,
  });
}
